import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Trash2, Clock, Save, Shield } from "lucide-react";
import { toast } from "sonner";
import { settingsApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TimePicker from "@/components/TimePicker";

// Sun-Sat display, but backend uses isoweekday (Mon=1..Sun=7).
// Map: display index 0 (Sun) → iso 7, index 1 (Mon) → iso 1, ...
const DAYS_DISPLAY = [
  { display: "Sun", iso: 7 },
  { display: "Mon", iso: 1 },
  { display: "Tue", iso: 2 },
  { display: "Wed", iso: 3 },
  { display: "Thu", iso: 4 },
  { display: "Fri", iso: 5 },
  { display: "Sat", iso: 6 },
];

function blockHours(b) {
  const [sh, sm] = b.start.split(":").map(Number);
  const [eh, em] = b.end.split(":").map(Number);
  return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
}

export default function AdminSettingsPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [blocks, setBlocks] = useState([
    { start: "09:00", end: "12:00" },
    { start: "13:00", end: "16:00" },
  ]);

  useEffect(() => {
    if (!me) return;
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    settingsApi
      .getWorkingConfig()
      .then((d) => {
        setWorkingDays(d.working_days || [1, 2, 3, 4, 5]);
        setBlocks(d.hour_blocks || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAdmin, me]);

  const toggleDay = (iso) => {
    setWorkingDays((prev) =>
      prev.includes(iso) ? prev.filter((d) => d !== iso) : [...prev, iso].sort(),
    );
  };

  const updateBlock = (idx, key, value) => {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? { ...b, [key]: value } : b)));
  };
  const addBlock = () => setBlocks((prev) => [...prev, { start: "09:00", end: "12:00" }]);
  const removeBlock = (idx) => setBlocks((prev) => prev.filter((_, i) => i !== idx));

  const totalHours = blocks.reduce((s, b) => s + blockHours(b), 0);

  const handleSave = async () => {
    // Basic client-side validation
    for (const b of blocks) {
      if (blockHours(b) <= 0) {
        toast.error(`Block ${b.start}–${b.end} must have end > start`);
        return;
      }
    }
    setSaving(true);
    try {
      await settingsApi.saveWorkingConfig({ working_days: workingDays, hour_blocks: blocks });
      toast.success(`Saved · ${totalHours}h per working day`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 flex items-center gap-2 text-zinc-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading working-hours settings…
      </div>
    );
  }

  if (me && !isAdmin) {
    return (
      <div className="max-w-md mx-auto text-center py-24 px-6">
        <Shield className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
        <h2 className="font-heading text-2xl font-light text-zinc-100 mb-2">
          Admin access required
        </h2>
        <p className="text-sm text-zinc-500 mb-6">
          Only admins can adjust workspace working hours.
        </p>
        <Link
          to="/launchpad"
          data-testid="back-to-launchpad-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-zinc-800 text-sm text-zinc-300 hover:text-amber-400 hover:border-amber-500/40 transition-colors"
        >
          Back to Launchpad
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-8" data-testid="admin-settings-page">
      <div className="mb-6">
        <div className="text-overline text-amber-500/80 mb-1 flex items-center gap-1.5">
          <Clock className="w-3 h-3" /> Admin · Global settings
        </div>
        <h1 className="font-heading text-4xl font-light text-zinc-100 leading-tight">
          Working Hours
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          One schedule for the whole team. Task scheduling, at-risk math, and the Studio Gantt all
          respect these settings.
        </p>
      </div>

      {/* Working days */}
      <section className="border border-zinc-800 rounded-md p-5 mb-4 bg-zinc-950/40">
        <h2 className="text-overline text-zinc-400 mb-3">Working Days</h2>
        <div className="grid grid-cols-7 gap-2">
          {DAYS_DISPLAY.map(({ display, iso }) => {
            const on = workingDays.includes(iso);
            return (
              <button
                key={iso}
                type="button"
                data-testid={`day-toggle-${display.toLowerCase()}`}
                onClick={() => toggleDay(iso)}
                aria-pressed={on}
                className={`h-16 rounded-sm border text-sm font-mono uppercase tracking-wide transition-colors ${
                  on
                    ? "bg-amber-500/15 border-amber-500/60 text-amber-300"
                    : "bg-zinc-950 border-zinc-800 text-zinc-600 hover:border-zinc-700"
                }`}
              >
                {display}
              </button>
            );
          })}
        </div>
      </section>

      {/* Working hour blocks */}
      <section className="border border-zinc-800 rounded-md p-5 mb-4 bg-zinc-950/40">
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-overline text-zinc-400">Working Hours (same every working day)</h2>
          <span className="text-xs font-mono text-zinc-500" data-testid="total-hours-display">
            {totalHours}h total per working day
          </span>
        </div>
        <div className="space-y-2">
          {blocks.map((b, i) => (
            <div
              key={i}
              className="flex items-center gap-3"
              data-testid={`hour-block-${i}`}
            >
              <TimePicker
                testIdPrefix={`block-${i}-start`}
                size="sm"
                value={b.start}
                onChange={(v) => updateBlock(i, "start", v)}
              />
              <span className="text-zinc-600 text-sm">to</span>
              <TimePicker
                testIdPrefix={`block-${i}-end`}
                size="sm"
                value={b.end}
                onChange={(v) => updateBlock(i, "end", v)}
              />
              <span className="text-xs font-mono text-zinc-500 w-16">
                {blockHours(b).toFixed(2)}h
              </span>
              {blocks.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeBlock(i)}
                  data-testid={`block-${i}-remove`}
                  className="text-zinc-600 hover:text-rose-400 transition-colors"
                  title="Remove block"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addBlock}
          data-testid="add-block"
          className="mt-3"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add another block
        </Button>
        <p className="text-[11px] text-zinc-500 mt-3">
          Add a second block for a lunch break (e.g. 9–12 + 1–4). Tasks scheduled at 1 PM will fill
          the afternoon block first, then spill to the next working day.
        </p>
      </section>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-settings"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-950"
        >
          {saving ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <Save className="w-3.5 h-3.5 mr-1.5" />
          )}
          Save settings
        </Button>
      </div>
    </div>
  );
}
